import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

// Available primary color palettes. Keep in sync with .theme-<name> classes
// defined in src/index.css and the swatches rendered in Settings.
export const THEME_COLORS = [
  { id: 'blue', swatch: '#2563eb' },
  { id: 'lightBlue', swatch: '#0ea5e9' },
  { id: 'green', swatch: '#10b981' },
  { id: 'pink', swatch: '#ec4899' },
  { id: 'lightPink', swatch: '#f472b6' },
  { id: 'yellow', swatch: '#eab308' },
  { id: 'orange', swatch: '#f97316' },
  { id: 'black', swatch: '#171717' },
  { id: 'white', swatch: '#f1f5f9' }
];

const MODES = ['light', 'dark', 'system'];

const MODE_KEY = 'life_manager_theme_mode';
const COLOR_KEY = 'life_manager_theme_color';

const ThemeContext = createContext({
  mode: 'system',
  color: 'blue',
  resolvedMode: 'light',
  setMode: () => {},
  setColor: () => {}
});

const readStored = (key, fallback) => {
  try {
    const v = window.localStorage.getItem(key);
    return v || fallback;
  } catch (_) {
    return fallback;
  }
};

const getSystemPref = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(() => {
    const stored = readStored(MODE_KEY, 'system');
    return MODES.includes(stored) ? stored : 'system';
  });
  const [color, setColorState] = useState(() => {
    const stored = readStored(COLOR_KEY, 'blue');
    return THEME_COLORS.some((c) => c.id === stored) ? stored : 'blue';
  });
  const [systemPref, setSystemPref] = useState(getSystemPref);

  // Listen to OS-level theme changes (for mode === 'system')
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemPref(e.matches ? 'dark' : 'light');
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const resolvedMode = mode === 'system' ? systemPref : mode;

  // Apply classes to <html>
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', resolvedMode === 'dark');

    // Remove any previous theme-* class, then add the current one.
    Array.from(root.classList)
      .filter((c) => c.startsWith('theme-'))
      .forEach((c) => root.classList.remove(c));
    root.classList.add(`theme-${color}`);
  }, [resolvedMode, color]);

  // Persist
  useEffect(() => {
    try {
      window.localStorage.setItem(MODE_KEY, mode);
    } catch (_) {}
  }, [mode]);
  useEffect(() => {
    try {
      window.localStorage.setItem(COLOR_KEY, color);
    } catch (_) {}
  }, [color]);

  const setMode = useCallback((next) => {
    if (MODES.includes(next)) setModeState(next);
  }, []);
  const setColor = useCallback((next) => {
    if (THEME_COLORS.some((c) => c.id === next)) setColorState(next);
  }, []);

  return (
    <ThemeContext.Provider value={{ mode, color, resolvedMode, setMode, setColor }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
